import { CurrentPayPlan, FlowType, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { Badge, Box, Checkbox, Icon, Link, Paper, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { useContext } from 'react';
import { MembershipAppStateContext } from '../MembershipAppState/MembershipAppState';
import { isAutopayRequired, isAutopayAllowed, isPaperlessOptOutAllowed } from '../../utils/helpers';
import { getFormattedDiscountedPrice } from '../../utils/formatters';
import { IPaperlessOptOut, PaperlessOptOutType } from '@lib/types';

export const EnrollAutopayDisclaimer = () => {
  const { membershipInfo, sessionData, updatePaperlessOption } = useContext(MembershipAppStateContext);
  const isGiftFlow = sessionData.flowType === FlowType.GIFT;
  const showAutopayEnrollment =
    !isAutopayRequired(membershipInfo!, sessionData.formData) && isAutopayAllowed(membershipInfo!);
  const selectedMembershipLevel = sessionData.formData.membershipLevel as MembershipLevel;
  const autoPayDiscountAmount =
    //@ts-ignore
    membershipInfo?.membershipCost?.[selectedMembershipLevel]?.annual?.autoPay?.discountAmount ?? 0;
  const showPaperlessOptOut = isPaperlessOptOutAllowed(membershipInfo?.paperlessOptOuts);
  const isMonthly = sessionData.formData.billPlan === CurrentPayPlan.RECURRINGMONTHLY;
  const showPaperlessOrAutopaySection = showAutopayEnrollment || showPaperlessOptOut;
  const physicalCard: IPaperlessOptOut | undefined = membershipInfo?.paperlessOptOuts?.find(
    (p) => p.type === 'PHYSICAL_CARD'
  );
  const paperless: IPaperlessOptOut | undefined = membershipInfo?.paperlessOptOuts?.find(
    (p) => p.type === 'PAPERLESS_BILLING'
  );
  const magazine: IPaperlessOptOut | undefined = membershipInfo?.paperlessOptOuts?.find((p) => p.type === 'MAGAZINE');

  return (
    <Stack sx={{ gap: '1rem' }}>
      {!isGiftFlow && (isAutopayRequired(membershipInfo!, sessionData.formData) || showAutopayEnrollment) && (
        <>
          <Paper
            elevation={8}
            sx={{
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1rem',
            }}
          >
            <Stack className="review-terms-section">
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap="0.5rem"
                  marginBottom="0.5rem"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" gap="0.5rem">
                    <Icon iconName="calendar-clock-duotone" sx={{ color: '#088BA6', fontSize: '1.5rem' }} />
                    <Typography className="card-title review-item-container" variant="b1Bold">
                      {showAutopayEnrollment ? <>Set up Auto Pay</> : <>Auto Pay required to join AAA</>}
                    </Typography>
                  </Stack>

                  {showAutopayEnrollment && (
                    <Badge severity="info">
                      <Typography variant="b2Medium" sx={{ color: '#7527B9' }}>
                        Save {getFormattedDiscountedPrice(autoPayDiscountAmount)}
                      </Typography>
                    </Badge>
                  )}
                </Stack>
                <Typography variant="b2Regular">
                  By clicking below, you agree to enroll in Auto Pay per the{' '}
                  <Link
                    className="benefit-link terms-condition-link"
                    href={`https://app.ace.aaa.com/payments/termsandconditions?variant=mmp&clubcode=${membershipInfo?.clubCode}`}
                    target="_blank"
                    variant="b2Regular"
                  >
                    Terms
                  </Link>
                  . Your membership will renew automatically each year for {!isMonthly ? <>1 year</> : <></>}
                  {isMonthly ? (
                    <>a 1-year term for annual dues option, or each month for monthly dues option,</>
                  ) : (
                    <></>
                  )}{' '}
                  at the amount specified using the payment method you enter next, unless you cancel Auto Pay prior to
                  your membership expiration date. To cancel Auto Pay, go to{' '}
                  <Link
                    className="benefit-link terms-condition-link"
                    href={`https://account.app.ace.aaa.com`}
                    target="_blank"
                    variant="b2Regular"
                  >
                    My Account
                  </Link>
                  , visit your{' '}
                  <Link
                    className="benefit-link terms-condition-link"
                    href={`https://www.ace.aaa.com/locations`}
                    target="_blank"
                    variant="b2Regular"
                  >
                    AAA branch
                  </Link>{' '}
                  or call{' '}
                  <Link
                    className="benefit-link terms-condition-link"
                    href={`tel:1-877-428-2277`}
                    target="_blank"
                    variant="b2Regular"
                  >
                    1-877-428-2277
                  </Link>
                  .
                </Typography>
              </Box>
              <Typography variant="b2Regular" sx={{ marginTop: '0.5rem' }}>
                *These recurring dues may change at renewal with notice (e.g., promotional rates/discounts expire).
              </Typography>
            </Stack>
            <Box className="enrollments-signup" id="checkbox-align">
              {showPaperlessOrAutopaySection ? (
                <>
                  {showPaperlessOptOut ? (
                    <>
                      <hr className="overview-line" />
                      <Stack className="paperless-options-content" sx={{ marginTop: '0.5rem' }}>
                        <Typography variant="h6Medium">Paperless options</Typography>
                        <Typography variant="b3Regular">
                          Opt out of receiving paper documents in the mail and access your AAA information digitally.
                          You can change your settings anytime in My Account. See{' '}
                          <Link
                            className="benefit-link paperless-terms-condition-link"
                            href={`https://www.ace.aaa.com/legal/paperless-terms-and-conditions.html`}
                            target="_blank"
                            variant="b3Regular"
                            sx={{ color: '#0156A3' }}
                          >
                            Terms and Conditions
                          </Link>
                          .
                        </Typography>
                      </Stack>
                      <Stack className="opt-out-checkboxes" gap="0.5rem">
                        <Typography variant="b1Medium" sx={{ margin: '1rem 1rem 0 0' }}>
                          I don't want to receive
                        </Typography>
                        <Stack padding={0} gap="0.5rem">
                          {paperless?.allowed && (
                            <Box
                              sx={{
                                '& .MuiCheckbox-FormControlLabel-Variant-text': {
                                  padding: '0 !important',
                                },
                              }}
                            >
                              <Checkbox
                                variant="text"
                                sx={{
                                  fontSize: '1rem',
                                }}
                                name="paperless"
                                value="paperless"
                                id="paperless-billing-or-documents-checkbox"
                                label="Paper billing or documents"
                                checked={sessionData.formData.paperlessBilling}
                                onClick={() => {
                                  updatePaperlessOption(
                                    PaperlessOptOutType.PAPERLESS_BILLING,
                                    !sessionData.formData.paperlessBilling
                                  );
                                }}
                              ></Checkbox>
                            </Box>
                          )}
                          {physicalCard?.allowed && (
                            <Box
                              sx={{
                                '& .MuiCheckbox-FormControlLabel-Variant-text': {
                                  padding: '0 !important',
                                },
                              }}
                            >
                              <Checkbox
                                variant="text"
                                name="physicalCard"
                                value="physicalCard"
                                id="physical-membership-card-checkbox"
                                sx={{ marginTop: '0', marginBottom: '0' }}
                                checked={sessionData.formData.declinePhysicalCard}
                                onClick={() => {
                                  updatePaperlessOption(
                                    PaperlessOptOutType.PHYSICAL_CARD,
                                    !sessionData.formData.declinePhysicalCard
                                  );
                                }}
                                label={
                                  <>
                                    <Typography sx={{ fontSize: '16px !important' }}>
                                      A physical membership card
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: '14px !important', color: '#707070', whiteSpace: 'nowrap' }}
                                    >
                                      Get your digital card on the Auto Club App
                                    </Typography>
                                  </>
                                }
                              ></Checkbox>
                            </Box>
                          )}
                          {magazine?.allowed && (
                            <Box
                              sx={{
                                '& .MuiCheckbox-FormControlLabel-Variant-text': {
                                  padding: '0 !important',
                                },
                              }}
                            >
                              <Checkbox
                                variant="text"
                                name="magazine"
                                value="magazine"
                                id="magazine-checkbox"
                                label={magazine.label}
                                sx={{ marginTop: '0' }}
                                checked={sessionData.formData.declineMagazine}
                                onClick={() => {
                                  updatePaperlessOption(
                                    PaperlessOptOutType.MAGAZINE,
                                    !sessionData.formData.declineMagazine
                                  );
                                }}
                              ></Checkbox>
                            </Box>
                          )}
                        </Stack>
                      </Stack>
                    </>
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                <></>
              )}
            </Box>
          </Paper>
        </>
      )}
    </Stack>
  );
};
